import { v4 as uuid } from 'uuid'
import { registerPasteHandler } from '../registry/block-paste-registry'
import { isRuleEmpty, isStepEmpty } from '../models/semantic-row-paste'
import { ensureRuleRowsWithIds, resolveFocusRowIndex } from '../models/semantic-row-model'
import { pasteIntoRules, pasteIntoStepsTitles } from '../models/semantic-row-paste'

const CAP = 100

function capItems(items, existingLength, extraSlots = 0) {
  const room = Math.max(0, CAP - existingLength + extraSlots)
  const slice = items.slice(0, room)
  return {
    slice,
    pasteWasCapped: items.length > room,
  }
}

function appendRules(parsed, currentData, focus) {
  const existing = ensureRuleRowsWithIds(
    Array.isArray(currentData.rules) ? currentData.rules : [],
  )
  const rowIndex = resolveFocusRowIndex(existing, focus)
  const willReplace =
    rowIndex >= 0 && isRuleEmpty(existing[rowIndex])
  const { slice, pasteWasCapped } = capItems(
    parsed.items,
    existing.length,
    willReplace ? 1 : 0,
  )
  const merged = pasteIntoRules(
    { ...parsed, items: slice },
    { ...currentData, rules: existing },
    focus,
  )
  return {
    transformVersion: 1,
    ...currentData,
    rules: merged.slice(0, CAP),
    ...(pasteWasCapped ? { pasteWasCapped: true } : {}),
  }
}

function appendSteps(parsed, currentData, focus) {
  const existing = Array.isArray(currentData.steps) ? currentData.steps : []
  const rowIndex = resolveFocusRowIndex(existing, focus)
  const willReplace =
    rowIndex >= 0 &&
    focus?.field === 'title' &&
    isStepEmpty(existing[rowIndex])
  const { slice, pasteWasCapped } = capItems(
    parsed.items,
    existing.length,
    willReplace ? 1 : 0,
  )
  const merged = pasteIntoStepsTitles(
    { ...parsed, items: slice },
    currentData,
    focus,
  )
  return {
    transformVersion: 1,
    ...currentData,
    steps: merged.slice(0, CAP),
    ...(pasteWasCapped ? { pasteWasCapped: true } : {}),
  }
}

function appendTimeline(parsed, currentData, _focus) {
  const normalized =
    currentData?.milestones != null
      ? currentData
      : { ...currentData, milestones: [] }
  const existing = Array.isArray(normalized.milestones)
    ? normalized.milestones
    : []
  const rawItems =
    parsed.type === 'timeline'
      ? parsed.items
      : parsed.items.map((text) => ({ text: String(text).trim() }))
  const { slice, pasteWasCapped } = capItems(rawItems, existing.length)
  const added = slice.map((item) => ({
    id: uuid(),
    title: item.text ?? item.title ?? '',
    notes: item.description ?? item.notes ?? '',
  }))
  const milestones = [...existing, ...added].slice(0, 7)
  return {
    transformVersion: 3,
    ...normalized,
    dayCount: milestones.length,
    milestones,
    ...(pasteWasCapped ? { pasteWasCapped: true } : {}),
  }
}

let registered = false

export function registerBlockPasteHandlers() {
  if (registered) return
  registered = true

  registerPasteHandler('block_rule', {
    canHandlePaste: (parsed) =>
      parsed.type === 'bullet' ||
      parsed.type === 'numbered' ||
      parsed.type === 'multiline',
    transformPaste: appendRules,
  })

  registerPasteHandler('block_card', {
    canHandlePaste: (parsed) =>
      parsed.type === 'bullet' ||
      parsed.type === 'numbered' ||
      parsed.type === 'multiline',
    transformPaste: appendRules,
  })

  registerPasteHandler('block_supporting_notes', {
    canHandlePaste: (parsed) =>
      parsed.type === 'bullet' ||
      parsed.type === 'numbered' ||
      parsed.type === 'multiline',
    transformPaste: appendRules,
  })

  registerPasteHandler('block_steps', {
    canHandlePaste: (parsed) =>
      parsed.type === 'numbered' || parsed.type === 'bullet',
    transformPaste: appendSteps,
  })

  registerPasteHandler('block_timeline', {
    canHandlePaste: (parsed) =>
      parsed.type === 'timeline' ||
      (parsed.type === 'multiline' && parsed.items.length >= 2),
    transformPaste: appendTimeline,
  })
}
